import { useLocation } from '@reach/router'
import { useState, useEffect } from 'react'
import { camelCase } from 'lodash'

import useExperimentInstance from 'amplitude/useExperimentInstance'

import { VARIANTS } from 'experiments/constants'
import useLocalStorage from './useLocalStorage'
import useSendDataToClarity from './useSendDataToClarity'
import objectStorage from './storage/objectStorage'

const transformAmplitudeKey = key =>
  key ? camelCase(`experiment-${key}`) : null

const updateForcedVariants = ({
  experimentKey,
  queryVariant,
  forcedVariants,
  setForcedVariants,
}) => {
  const useOriginal = queryVariant === '0'

  if (useOriginal) {
    setForcedVariants({
      ...forcedVariants,
      [experimentKey]: undefined,
    })
  } else if (queryVariant) {
    setForcedVariants({
      ...forcedVariants,
      [experimentKey]: queryVariant,
    })
  }
}

const FORCED_VARIANT = 'experimentForcedVariant'
const EXPOSED_EXPERIMENTS = 'exposedExperiments'

const useExperiment = (amplitudeKey, enabled = true) => {
  const location = useLocation()
  const [variantValue, setVariantValue] = useState()
  const [forcedVariants, setForcedVariants] = useLocalStorage(
    FORCED_VARIANT,
    {},
  )
  const sendDataToClarity = useSendDataToClarity()
  const experimentInstance = useExperimentInstance()
  const instanceAvailable = !!experimentInstance
  const experimentKey = transformAmplitudeKey(amplitudeKey)
  const searchParams = new URLSearchParams(location.search)
  const query = searchParams.get(experimentKey)
  const queryVariant = query || forcedVariants[experimentKey]
  const forceVariant = !!queryVariant
  const exposedExperimentsStorage = objectStorage(EXPOSED_EXPERIMENTS)

  useEffect(() => {
    if (forcedVariants[experimentKey] !== query && query) {
      updateForcedVariants({
        experimentKey,
        queryVariant,
        forcedVariants,
        setForcedVariants,
      })
    }
  }, [experimentKey, forcedVariants, query, queryVariant, setForcedVariants])

  useEffect(() => {
    if (!enabled) {
      return () => null
    }

    if (forceVariant) {
      setVariantValue(queryVariant)
    } else if (instanceAvailable) {
      const value = experimentInstance.variant(amplitudeKey)?.value

      setVariantValue(value)
      sendDataToClarity(camelCase(amplitudeKey), value)
    }

    return () => null
  }, [
    amplitudeKey,
    enabled,
    experimentInstance,
    forceVariant,
    instanceAvailable,
    queryVariant,
    sendDataToClarity,
  ])

  useEffect(() => {
    if (instanceAvailable && enabled && variantValue !== VARIANTS.off) {
      exposedExperimentsStorage.add(amplitudeKey, variantValue)
    } else if (!enabled || variantValue === VARIANTS.off) {
      exposedExperimentsStorage.remove(amplitudeKey)
    }
  }, [instanceAvailable, enabled, amplitudeKey, variantValue]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isExperimentReady: instanceAvailable && !!variantValue,
    variantValue,
  }
}

export default useExperiment
